import * as signalR from "@microsoft/signalr";
import { useEffect, useState } from "react";

// With a url and a accessToken obtains a signalr hub connection that should
// be placed on context and make available globally in the app for component subscriptions
// includes auto reconnecting an auto stopping on component unmount
export default function useSignalR(
  url: string,
  accessToken: string | undefined
): signalR.HubConnection | undefined {
  const [connection, setConnection] = useState<
    signalR.HubConnection | undefined
  >(undefined);

  useEffect(() => {
    const builder = new signalR.HubConnectionBuilder().withAutomaticReconnect();

    if (accessToken !== undefined) {
      builder.withUrl(url, { accessTokenFactory: () => accessToken });
    } else {
      return;
    }

    const connection = builder.build();

    connection
      .start()
      .then(() => setConnection(connection))
      .catch((error) => {
        if (window.rg4js !== undefined) {
          window.rg4js("send", {
            error,
          });
        }
      });

    return () => {
      if (connection.state === signalR.HubConnectionState.Connected) {
        connection.stop().then(() => setConnection(undefined));
      } else {
        setConnection(undefined);
      }
    };
  }, [url, accessToken]);

  return connection;
}

// To be used with useSignalR. Attaches a handler to an existing signalr hub connection
// subscriptions will be removed when the component using the hook unmounts or if
// the methodName or newMethod change. Make sure newMethod is using useCallback
export function useSignalRHandler(
  connection: signalR.HubConnection | undefined,
  methodName: string,
  newMethod: (...args: any[]) => void
): void {
  const { state } = connection || {};
  useEffect(() => {
    if (connection && state === signalR.HubConnectionState.Connected) {
      connection.on(methodName, newMethod);
      return () => {
        if (connection) {
          connection.off(methodName, newMethod);
        }
      };
    }
  }, [connection, state, methodName, newMethod]);
}
